import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ConversationItem,
  CreateFromPublicConversationPayload,
  FileItem,
  ListConversationResponse,
  MessageItem,
  PublicListConversationsParams,
  PublicListMessagesParams,
  PublicUnseenMetrics,
  PublicUserDataModel,
  SendMessageFromPublicPayload
} from '@appTypes/chat-api.types';

import { toHttpParams } from '@core/misc/misc.utils';
import { environment } from '@env/environment';
import { map, Observable, of, switchMap } from 'rxjs';
import { ChatOpenConversationParams } from '../chat.type';

@Injectable({
  providedIn: 'root'
})
export class ChatApiService {
  readonly #httpClient = inject(HttpClient);
  readonly #chatApiEndpoint: string = environment.urls.chatApi + '/';

  public getUserData() {
    return this.#httpClient.get<PublicUserDataModel>(this.#chatApiEndpoint + 'whoami');
  }

  public conversationList(params?: PublicListConversationsParams) {
    const httpParams = toHttpParams(params);
    return this.#httpClient.get<ListConversationResponse>(this.#chatApiEndpoint + 'conversations', {
      params: httpParams
    });
  }

  public conversationCreate(payload: CreateFromPublicConversationPayload) {
    return this.#httpClient.post<ConversationItem>(this.#chatApiEndpoint + 'conversations', payload);
  }

  public openConversation({
    reference,
    referenceId,
    initialMessage,
    title
  }: ChatOpenConversationParams): Observable<{ conversation: ConversationItem; messages: MessageItem[] }> {
    return this.conversationList({ reference, referenceIds: [referenceId], limit: 1, skip: 0 }).pipe(
      switchMap((response) => {
        if (response.items.length && response.items?.[0]?._id) {
          return this.messages(response.items[0]._id).pipe(
            map((messages) => ({ conversation: response.items[0], messages }))
          );
        }
        return this.conversationCreate({ reference, referenceId, initialMessage, title }).pipe(
          map((conversation) => ({
            conversation,
            messages: initialMessage && conversation.lastMessage ? [conversation.lastMessage] : []
          }))
        );
      })
    );
  }
  public loadConversation({
    reference,
    referenceId
  }: ChatOpenConversationParams): Observable<{ conversation: ConversationItem; messages: MessageItem[] } | null> {
    return this.conversationList({ reference, referenceIds: [referenceId], limit: 1, skip: 0 }).pipe(
      switchMap((response) => {
        if (response.items.length && response.items?.[0]?._id) {
          return this.messages(response.items[0]._id).pipe(
            map((messages) => ({ conversation: response.items[0], messages }))
          );
        }
        return of(null);
      })
    );
  }

  public conversationMarkAsSeen(conversationId: string) {
    return this.#httpClient.post<ConversationItem>(
      this.#chatApiEndpoint + 'conversations/' + conversationId + '/mark-as-seen',
      {}
    );
  }

  public messages(conversationId: string, params?: Omit<PublicListMessagesParams, 'conversationId'>) {
    const httpParams = toHttpParams(params);
    return this.#httpClient.get<MessageItem[]>(
      this.#chatApiEndpoint + 'conversations/' + conversationId + '/messages',
      {
        params: httpParams
      }
    );
  }
  public messageSend(conversationId: string, payload: SendMessageFromPublicPayload) {
    return this.#httpClient.post<MessageItem>(
      this.#chatApiEndpoint + 'conversations/' + conversationId + '/messages',
      payload
    );
  }
  public unseenMessagesCount() {
    return this.#httpClient.get<PublicUnseenMetrics>(this.#chatApiEndpoint + 'unseen-messages-count');
  }

  public uploadAttachment(conversationId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.#httpClient.post<FileItem>(
      this.#chatApiEndpoint + `conversations/${conversationId}/upload-attachment`,
      formData
    );
  }
  public downloadAttachment(conversationId: string, fileId: string) {
    return this.#httpClient.get(this.#chatApiEndpoint + `conversations/${conversationId}/attachments/${fileId}`, {
      responseType: 'blob'
    });
  }
}
